import { PrintIcon } from '@/common/icons/PrintIcon';
import { IUser } from '@/common/models';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { formatDate } from '@/lib/format-date';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface IProps {
  children: ReactNode;
  contract: any;
  printDocs: () => void;
  organization: IUser | null;
  id: string;
  loadId: string;
  created_at: string;
  farmer_tax_number: string;
}

export const PrintDocsModal: FC<IProps> = ({
  children,
  printDocs,
  contract,
  organization,
  id,
  loadId,
  created_at,
  farmer_tax_number,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleClose = (open: boolean) => {
    if (!open && loadId) {
      return navigate('/loads');
    }
  };

  return (
    <Dialog onOpenChange={(open) => handleClose(open)}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      {loadId && (
        <DialogContent className="max-w-[880px] text-black">
          <div className="flex flex-col space-y-8 p-6">
            <div className="flex justify-between">
              <div className="flex flex-col space-y-1 text-sm">
                <p>
                  {t('grain.receiving.enterprise')}:{' '}
                  {contract?.organization_name || '"Sangzor" MCHJ'}
                </p>
                <p>
                  {t('grain.storage.place')}:{' '}
                  {organization?.place_name || 'Korxona qoshi (Andijon)'}
                </p>
                <p>
                  {t('delivering.farmer.name')}:{' '}
                  {contract?.farmer_name || 'OYBEK F/X'}
                </p>
              </div>

              <div className="flex flex-col items-end space-y-1">
                <p className="text-lg font-bold">DQIM-3</p>
                <p className="text-sm">
                  {t('delivering.farmer.stir')}:{' '}
                  <span className="font-semibold">
                    {farmer_tax_number || '321234567'}
                  </span>
                </p>
                <p className="text-sm">
                  {t('id.number.colon')}{' '}
                  <span className="font-bold">{id || '-'}</span>
                </p>
              </div>
            </div>

            <div className="flex justify-end">
              <p className="text-sm font-medium">
                {formatDate(created_at) || '19.11.2025'}
              </p>
            </div>

            <div className="flex flex-col items-center space-y-1">
              <p className="font-medium text-sm">
                {t('grain.analysis.results')}
              </p>
              <DialogTitle className="text-2xl font-bold">
                {t('information.note')}
              </DialogTitle>
            </div>

            <div className="flex justify-between items-center">
              <p className="text-lg font-semibold">
                {t('laboratory.conclusion')}
              </p>
              <p className="text-sm font-medium">
                {organization?.district_name || "Ulug'nor tumani"}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-x-16 gap-y-5 text-sm">
              <div className="flex items-end">
                <span className="font-medium whitespace-nowrap">
                  {t('grain.crop.type')}
                </span>
                <div className="flex-grow border-b-2 border-gray-600 mx-3" />
              </div>
              <div className="flex items-end">
                <span className="font-medium whitespace-nowrap">
                  {t('grain.pollution')}
                </span>
                <div className="flex-grow border-b-2 border-gray-600 mx-3" />
                <span>%</span>
              </div>

              <div className="flex items-end">
                <span className="font-medium whitespace-nowrap">
                  {t('grain.humidity')}
                </span>
                <div className="flex-grow border-b-2 border-gray-600 mx-3" />
                <span>%</span>
              </div>
              <div className="flex items-end">
                <span className="font-medium whitespace-nowrap">
                  {t('grain.naturasi')}
                </span>
                <div className="flex-grow border-b-2 border-gray-600 mx-3" />
                <span>gr</span>
              </div>

              <div className="flex items-end">
                <span className="font-medium whitespace-nowrap">
                  {t('grain.mixture')}
                </span>
                <div className="flex-grow border-b-2 border-gray-600 mx-3" />
                <span>%</span>
              </div>
              <div className="flex items-end">
                <span className="font-medium whitespace-nowrap">
                  {t('grain.vitreous')}
                </span>
                <div className="flex-grow border-b-2 border-gray-600 mx-3" />
                <span>%</span>
              </div>

              <div className="flex items-end">
                <span className="font-medium whitespace-nowrap">
                  {t('grain.gluten')}
                </span>
                <div className="flex-grow border-b-2 border-gray-600 mx-3" />
                <span>%</span>
              </div>
              <div className="flex items-end">
                <span className="font-medium whitespace-nowrap">
                  {t('grain.class')}
                </span>
                <div className="flex-grow border-b-2 border-gray-600 mx-3" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-x-16 gap-y-5 text-sm mt-4">
              <div className="flex items-end">
                <span className="font-medium whitespace-nowrap">
                  {t('farm.representative')}
                </span>
                <div className="flex-grow border-b-2 border-gray-600 ml-3" />
              </div>
              <div />

              <div className="flex items-end">
                <span className="font-medium whitespace-nowrap">
                  {t('senior.laboratory.assistant')}
                </span>
                <div className="flex-grow border-b-2 border-gray-600 ml-3" />
              </div>
              <div className="flex items-end">
                <span className="font-medium whitespace-nowrap">
                  {t('laboratory.manager')}
                </span>
                <div className="flex-grow border-b-2 border-gray-600 ml-3" />
              </div>
            </div>
          </div>

          <DialogFooter className="absolute -bottom-20 left-[50%] translate-x-[-50%]">
            <Button
              variant="outline"
              onClick={printDocs}
              className="font-medium flex-h-center"
            >
              <PrintIcon />
              <span>{t('print')}</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>
  );
};
